'use client'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/styles'

type TabsVariant = 'pill' | 'underline'

const TabsContext = React.createContext<TabsVariant>('pill')

type TabsProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
	variant?: TabsVariant
}

const Tabs = React.forwardRef<React.ComponentRef<typeof TabsPrimitive.Root>, TabsProps>(
	({ variant = 'pill', ...props }, ref) => (
		<TabsContext.Provider value={variant}>
			<TabsPrimitive.Root ref={ref} {...props} />
		</TabsContext.Provider>
	),
)
Tabs.displayName = TabsPrimitive.Root.displayName

const tabsListVariants = cva(
	'inline-flex h-auto items-center justify-center text-muted-foreground',
	{
		variants: {
			variant: {
				pill: 'w-fit gap-1 rounded-tab-list bg-neutral-light-100 p-2',
				underline: 'gap-10 border-b border-brand-black',
			},
		},
	},
)

const tabsTriggerVariants = cva(
	'inline-flex items-center cursor-pointer data-[state=active]:cursor-default justify-center h-auto text-brand-black whitespace-nowrap ring-offset-background transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				pill: 'text-xl font-bold rounded-tab-trigger px-6 py-3 data-[state=active]:bg-brand-black data-[state=active]:text-neutral-light-100 data-[state=active]:shadow-sm',
				underline:
					'text-base py-2 border-b-2 border-transparent data-[state=active]:border-brand-black',
			},
		},
	},
)

const TabsList = React.forwardRef<
	React.ComponentRef<typeof TabsPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
	const variant = React.useContext(TabsContext)
	return (
		<TabsPrimitive.List
			ref={ref}
			className={cn(tabsListVariants({ variant }), className)}
			{...props}
		/>
	)
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
	React.ComponentRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
	const variant = React.useContext(TabsContext)
	return (
		<TabsPrimitive.Trigger
			ref={ref}
			className={cn(tabsTriggerVariants({ variant }), className)}
			{...props}
		/>
	)
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
	React.ComponentRef<typeof TabsPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn(
			'mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
			className,
		)}
		{...props}
	/>
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
